import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RadarResponseSchema, type PresenceUpdate, type RadarResponse } from '@web/shared';
import { apiRequest } from '@/lib/api';

export const radarQueryKey = (spotId: string | null) => ['radar', spotId] as const;

/** Polls the Radar for a Spot every 5s — plus every heartbeat (`usePresenceUpdate`,
 * ~10s while checked in) seeds this same cache with its own fresh `people`, so in
 * practice the list updates roughly every 5s regardless of which loop fires,
 * with no manual reload needed. Refetches immediately on tab focus too. */
export function useRadar(spotId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: radarQueryKey(spotId),
    enabled: !!spotId && enabled,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) =>
      apiRequest(`/api/presence/radar/${spotId}`, { schema: RadarResponseSchema, signal }),
  });
}

export function usePresenceUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PresenceUpdate) =>
      apiRequest('/api/presence/update', { method: 'POST', body, schema: RadarResponseSchema }),
    onSuccess: (data: RadarResponse, variables) => {
      // The heartbeat response already carries a fresh radar read — use it
      // immediately instead of waiting for the next poll tick.
      qc.setQueryData(radarQueryKey(variables.spotId), data);
    },
  });
}
